/**
 * frontend/src/pages/valuation/_CompanyRows.tsx
 *
 * VAL-COMPANY-PROFILE-1B — the add/remove row editor the Company step uses
 * for revenue mix, owners and key people. Generic over the row shape: each
 * column is a text or number input keyed into the row. A column with `total`
 * set gets a running "Total N%" under the list, red once it passes 100 (the
 * screen still lets Save send it — the server's message is the error).
 * `rowErrors[i]` outlines row i in red and shows its message under that row.
 */
import { useId } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { HelpDot } from '@/components/ui/help-dot'
import { HELP_TEXTS, type HelpKey } from './_helpTexts'

export interface RowColumn<T> {
  key: keyof T & string
  label: string
  placeholder: string
  numeric?: boolean
}

export function CompanyRows<T extends Record<string, string>>({
  title,
  singular,
  rows,
  columns,
  blank,
  total,
  rowErrors,
  note,
  addLabel,
  help,
  onChange,
}: {
  title: string
  singular: string
  rows: T[]
  columns: Array<RowColumn<T>>
  blank: T
  total?: number
  rowErrors?: Array<string | null>
  /** Read-only text beside a row (e.g. where a filled value came from). */
  note?: (row: T) => string[]
  /** Button text; defaults to `Add {singular}`. */
  addLabel?: string
  /** POL-B item 5: the approved help text beside the title. */
  help?: HelpKey
  onChange: (rows: T[]) => void
}) {
  const lower = singular.toLowerCase()
  const titleId = useId()
  const edit = (i: number, key: keyof T & string, value: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)))

  return (
    <fieldset className="space-y-2" aria-labelledby={titleId}>
      <legend className="flex items-center gap-1 text-sm font-medium text-gray-900">
        <span id={titleId}>{title}</span>
        {help && <HelpDot label={title} paragraphs={HELP_TEXTS[help]} testId={`info-${help}`} />}
      </legend>
      {rows.map((row, i) => (
        <div key={i} className="space-y-1">
        <div className={rowErrors?.[i] ? 'flex items-center gap-2 rounded-md border border-red-500 p-1' : 'flex items-center gap-2'}>
          {columns.map(col => (
            <Input
              key={col.key}
              aria-label={`${singular} ${col.label} ${i + 1}`}
              placeholder={col.placeholder}
              type={col.numeric ? 'number' : 'text'}
              className={col.numeric ? 'w-28' : 'min-w-0 flex-1'}
              value={row[col.key]}
              onChange={e => edit(i, col.key, e.target.value)}
            />
          ))}
          {note?.(row).map(t => <span key={t} className="text-xs text-gray-500">{t}</span>)}
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Remove ${lower} ${i + 1}`}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            Remove
          </Button>
        </div>
        {rowErrors?.[i] && <p className="text-sm font-medium text-red-600">{rowErrors[i]}</p>}
        </div>
      ))}
      {total !== undefined && rows.length > 0 && (
        <p className={total > 100 ? 'text-sm font-medium text-red-600' : 'text-sm text-gray-500'}>
          {`Total ${total}%`}
        </p>
      )}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { ...blank }])}>
        {addLabel ?? `Add ${lower}`}
      </Button>
    </fieldset>
  )
}
